import apiClient from "@/api/apiClient";
import Vue from "vue";

// Welke facet-types horen bij een gekozen dashboard-type. De gecombineerde knop
// "professionalisering / duurzame inzetbaarheid" toont beide types samen.
const FACET_TYPES_BY_TYPE = {
    starters: ['organisation'],
    professional: ['professional'],
    sustainable_employability: ['sustainable_employability'],
    professional_sustainable: ['professional', 'sustainable_employability'],
};

// Welke organisationType-waarden horen bij een gekozen dashboard-type.
const ORGANISATION_TYPES_BY_TYPE = {
    starters: ['starters'],
    professional: ['professional'],
    sustainable_employability: ['sustainable_employability'],
    professional_sustainable: ['professional', 'sustainable_employability'],
};

function normalizeFacetType (facet) {
    const raw = facet && facet.type != null ? String(facet.type) : '';
    return raw.trim().toLowerCase() || 'organisation';
}

function normalizeChoice (choice) {
    return {
        id: choice.id,
        value: choice.value,
        description: choice.description || null,
        ids: [choice.id],
    };
}

/**
 * Bouwt de facetten voor de huidige weergave. Zonder gekozen type worden alle
 * facetten ongewijzigd getoond. Met een type worden alleen de relevante facetten
 * meegenomen; gelijknamige facetten (en hun keuzes) worden samengevoegd zodat de
 * gecombineerde knop geen dubbele thema's/filters toont. Elke keuze houdt via
 * `ids` de onderliggende keuze-id's bij, zodat filteren over beide types werkt.
 */
function buildViewFacets (facets, type) {
    const all = facets || [];

    // Met een gekozen type nemen we alleen de relevante facetten mee; zonder type
    // (bijv. wanneer de pagina niet via een dashboardknop is geopend) alle facetten.
    // In beide gevallen worden gelijknamige facetten samengevoegd, zodat er altijd
    // één "Thema"-facet is en het overzicht nooit dubbele thema's/filters toont.
    const relevant = type
        ? all.filter(facet => (FACET_TYPES_BY_TYPE[type] || [type]).includes(normalizeFacetType(facet)))
        : all;

    const groups = [];
    relevant.forEach(facet => {
        let group = groups.find(g => g.name === facet.name);
        if (!group) {
            group = {
                id: facet.id,
                name: facet.name,
                type: type || facet.type,
                sortId: facet.sortId,
                choices: [],
            };
            groups.push(group);
        }

        (facet.choices || []).forEach(choice => {
            let merged = group.choices.find(c => c.value === choice.value);
            if (!merged) {
                merged = normalizeChoice(choice);
                group.choices.push(merged);
                return;
            }

            if (!merged.ids.includes(choice.id)) {
                merged.ids.push(choice.id);
            }
            if (!merged.description && choice.description) {
                merged.description = choice.description;
            }
        });
    });

    return groups;
}

// initial state
const state = {
    facets: [],
    viewFacets: [],
    organisations: [],
    organisationsLastUpdatedAt: null,
    filters: {
        facetChoices: [],
        searchQuery: null,
        organisationType: null,
        organisationTypes: [],
    },
    filteredOrganisations: [],
    organisationPage: 1,
    loadedOrganisations: [],
}

// getters
const getters = {
    visibleFacets: (state) => state.viewFacets,
}

// actions
const actions = {
    getOrganisation({commit, state}, uuid) {
        // const updateInterval = 15 * 60 * 1000; // 15 min
        const updateInterval = 0; // Op verzoek van Ellen op 0 gezet.
        // Ignore if already loaded  within the interval.
        const loadedOrganisation = state.loadedOrganisations.find(organisation => organisation.uuid === uuid);
        if (loadedOrganisation && loadedOrganisation.loadedAt > (new Date()).getTime() - updateInterval) {
            return loadedOrganisation;
        }

        return apiClient.get('/organisaties/'+uuid)
            .then(response => {
                let organisation = response.data;
                organisation.loadedAt = (new Date()).getTime();
                commit('LOAD_ORGANISATION', organisation);
                return organisation;
            });
    },
    updateOrganisations({commit, state}) {
        const updateInterval = 15 * 60 * 1000; // 15 min
        // Ignore if already updated within the interval.
        if (state.organisationsLastUpdatedAt !== null && state.organisationsLastUpdatedAt > (new Date()).getTime() - updateInterval) {
            return;
        }

        return apiClient.get('/organisaties')
            .then(response => {
                commit('SET_ORGANISATIONS', response.data.organisations);
                commit('SET_FACETS', response.data.facets);
                commit('UPDATE_FILTERED_ORGANISATIONS');
            });
    },
    updateFacets({commit}) {
        return apiClient.get('/organisaties/facetten')
            .then(response => {
                commit('SET_FACETS', response.data);
                commit('UPDATE_FILTERED_ORGANISATIONS');
            });
    },
    updateFacetFilter({commit}, {facet, choices}) {
        commit('SET_FACET_FILTER', {facet, choices});
        commit('UPDATE_FILTERED_ORGANISATIONS');
    },
    updateSearchQuery({commit}, searchQuery) {
        commit('SET_SEARCH_QUERY', searchQuery);
        commit('UPDATE_FILTERED_ORGANISATIONS');
    },
    updateOrganisationPage({commit}, organisationPage) {
        commit('SET_ORGANISATION_PAGE', organisationPage);
    },
    updateOrganisationType({commit}, organisationType) {
        commit('SET_ORGANISATION_TYPE', organisationType);
        commit('CLEAR_FACET_FILTERS');
        commit('UPDATE_FILTERED_ORGANISATIONS');
    },
    updateOrganisationTypeFilter({commit}, organisationTypes) {
        commit('SET_ORGANISATION_TYPE_FILTER', organisationTypes);
        commit('UPDATE_FILTERED_ORGANISATIONS');
    },
    // Aanmelding voor de wachtlijst van een thema waar nog geen aanbod voor is.
    registerWaitlist(_, { theme, name, email, school }) {
        const formData = new FormData();
        formData.append('theme', theme);
        formData.append('name', name);
        formData.append('email', email);
        if (school) formData.append('school', school);

        return apiClient.post('/wachtlijst', formData).then(response => response.data);
    },
}

// mutations
const mutations = {
    LOAD_ORGANISATION(state, organisation) {
        // Remove if already loaded before.
        const existingOrganisationIndex = state.loadedOrganisations.findIndex(p => p.uuid === organisation.uuid);
        if (existingOrganisationIndex !== -1) {
            state.loadedOrganisations.splice(existingOrganisationIndex, 1);
        }

        state.loadedOrganisations.push(organisation);
    },
    SET_ORGANISATIONS(state, organisations) {
        state.organisations = organisations;
        state.organisationsLastUpdatedAt = (new Date()).getTime();
    },
    SET_FACETS(state, facets) {
        state.facets = facets;
        state.viewFacets = buildViewFacets(state.facets, state.filters.organisationType);
    },
    UPDATE_FILTERED_ORGANISATIONS(state) {
        let organisations = state.organisations;

        if (state.filters.organisationType) {
            const wantedOrganisationTypes = ORGANISATION_TYPES_BY_TYPE[state.filters.organisationType] || [state.filters.organisationType];
            organisations = organisations.filter(o => wantedOrganisationTypes.includes(o.organisationType));
        }

        // Het "Type dienstverlener"-filter: alleen dienstverleners met een aangevinkt type tonen.
        const selectedOrganisationTypes = state.filters.organisationTypes || [];
        if (selectedOrganisationTypes.length > 0) {
            organisations = organisations.filter(o => selectedOrganisationTypes.includes(o.organisationType));
        }

        if (state.filters.searchQuery) {
            const searchQuery = state.filters.searchQuery.toLowerCase();
            organisations = organisations.filter(organisation =>
                organisation.name.toLowerCase().includes(searchQuery)
                || organisation.mainActivity.toLowerCase().includes(searchQuery)
                || organisation.email.toLowerCase().includes(searchQuery)
                || organisation.phoneNumber.toLowerCase().includes(searchQuery)
            );
        }

        // Filteren op de (samengevoegde) weergavefacetten: een geselecteerde keuze
        // omvat alle onderliggende keuze-id's, ook wanneer beide types zijn samengevoegd.
        state.viewFacets.forEach(facet => {
            const selectedChoiceIds = state.filters.facetChoices[facet.id];
            if (!selectedChoiceIds || selectedChoiceIds.length === 0) {
                return;
            }

            const underlyingChoiceIds = [];
            selectedChoiceIds.forEach(selectedId => {
                const choice = facet.choices.find(c => c.id === selectedId);
                if (choice) {
                    choice.ids.forEach(id => underlyingChoiceIds.push(id));
                }
            });

            organisations = organisations.filter(organisation =>
                underlyingChoiceIds.some(id => !!organisation.facetChoices.find(choice => choice.id === id))
            );
        });

        if (state.filteredOrganisations.length !== organisations.length) {
            state.organisationPage = 1;
        }

        state.filteredOrganisations = organisations;
    },
    SET_FACET_FILTER(state, {facet, choices}) {
        Vue.set(state.filters.facetChoices, facet.id, choices);
    },
    SET_ORGANISATION_TYPE_FILTER(state, organisationTypes) {
        Vue.set(state.filters, 'organisationTypes', organisationTypes || []);
    },
    SET_SEARCH_QUERY(state, searchQuery) {
        Vue.set(state.filters, 'searchQuery', searchQuery);
    },
    SET_ORGANISATION_PAGE(state, organisationPage) {
        state.organisationPage = organisationPage;
    },
    SET_ORGANISATION_TYPE(state, organisationType) {
        Vue.set(state.filters, 'organisationType', organisationType);
        state.viewFacets = buildViewFacets(state.facets, organisationType);
    },
    CLEAR_FACET_FILTERS(state) {
        state.filters.facetChoices = [];
    },
}

export default {
    state,
    getters,
    actions,
    mutations,
    namespaced: true
}
